import { ArrowUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { FlaggedObjectiveList } from '@/components/custom/ogsm/flagged-objective-list'
import { ogsmErrorMessage, ogsmPublishRefused } from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { usePublishOgsmSession } from '@/hooks/use-ogsm'
import type { OgsmBoard } from '@/types/api'

/** Asks first. A refusal stays in the dialog with the backend's reason. */
export function PublishSessionDialog({
  companyId,
  board,
  open,
  onOpenChange,
  onPublished,
}: {
  companyId: string
  board: OgsmBoard
  open: boolean
  onOpenChange: (open: boolean) => void
  onPublished: () => void
}) {
  const { session } = board
  const publish = usePublishOgsmSession(companyId, session.id, onPublished)
  const flagged = board.objectives.filter((o) => o.includeInScorecard)
  const changeOpen = (next: boolean) => {
    if (!next) publish.reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="max-w-125 px-4 py-5">
        <DialogHeader className="mb-4">
          <DialogTitle>
            {OGSM_COPY.publishTitle(session.fiscalYear)}
          </DialogTitle>
          <DialogDescription>{OGSM_COPY.publishBody}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3.5">
          <FlaggedObjectiveList
            label={OGSM_COPY.enteringScorecard}
            objectives={flagged}
            emptyText={OGSM_COPY.noneFlagged}
          />
          <div className="rounded-md border border-primary/10 bg-primary/5 px-3 py-2.5">
            <Typography variant="body-sm" className="text-primary/60">
              {OGSM_COPY.earlierYearNote}
            </Typography>
          </div>
          {publish.error && (
            <div
              role="alert"
              className="flex flex-col gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5"
            >
              {ogsmPublishRefused(publish.error) ? (
                <>
                  <Typography
                    variant="body-sm"
                    className="font-medium text-destructive"
                  >
                    {OGSM_COPY.publishRefused}
                  </Typography>
                  <Typography variant="body-sm" className="text-destructive">
                    {ogsmErrorMessage(publish.error)}
                  </Typography>
                  <Typography variant="body-sm" className="text-destructive">
                    {OGSM_COPY.publishRefusedHelp}
                  </Typography>
                </>
              ) : (
                <Typography variant="body-sm" className="text-destructive">
                  {ogsmErrorMessage(publish.error)}
                </Typography>
              )}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => changeOpen(false)}>
            {OGSM_COPY.keepAsDraft}
          </Button>
          <Button
            variant="secondary"
            className="text-white hover:text-white"
            disabled={publish.isPending}
            onClick={() =>
              publish.mutate(undefined, {
                onSuccess: () => changeOpen(false),
              })
            }
          >
            <ArrowUp />
            {publish.isPending
              ? OGSM_COPY.publishing
              : OGSM_COPY.publishToScorecard}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
